import React, { useEffect, useState } from "react";
import { Col, Modal, ModalBody, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styles from "./styles.module.scss";
import RewardItemTable from "../../Table/RewardItem/Table";
import AddRewardItemModal from "../AddRewardItem/AddReward";
import FormSelect from "../../global/FormSelect/FormSelect";
import FormInput from "../../global/FormInput/FormInput";
import { addReward, editReward } from "../../../../../helpers/service";
import {
  addNewReward,
  updateReward,
} from "../../../../Redux/Reducer/rewardReducer";

const initialRewardItem = {
  itemName: "",
  itemDescription: "",
  itemPointValue: "",
  itemRedeemURL: "",
  rewardItemQuantity: "",
  itemType: "",
};

const AddProductModal = ({
  openModal,
  toggle,
  setLoading,
  editRewardId,
  selectedSellerId,
}) => {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.appReducer);
  const { id, idToken, role } = useSelector((state) => state.authReducer);
  const { rewards } = useSelector((state) => state.rewardReducer);

  const [rewardType, setRewardType] = useState("General Reward");
  const [rewardName, setRewardName] = useState("");
  const [selectedProducts, setSelectedProducts] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("unlimited");
  const [rewardItems, setRewardItems] = useState([initialRewardItem]);
  const [isRewardItemModalOpen, setIsRewardItemModalOpen] = useState(false);
  const [newProducts, setNewProducts] = useState([]);

  useEffect(() => {
    if (selectedSellerId && role === "admin") {
      const newProducts = products.filter((p) => p.userId === selectedSellerId);

      setNewProducts(newProducts);
    } else {
      setNewProducts(products);
    }
  }, [selectedSellerId]);

  useEffect(() => {
    if (editRewardId) {
      const reward = rewards.find((reward) => reward.rewardId === editRewardId);
      if (reward) {
        setRewardName(reward.rewardName);
        setRewardType(reward.rewardType);
        setSelectedProducts(reward.productId);
        setStartDate(reward.startDate);
        setEndDate(reward.endDate);
        setRewardItems(
          reward.rewardItems?.length > 0
            ? reward.rewardItems
            : [initialRewardItem]
        );
      }
    } else {
      resetForm();
    }
  }, [editRewardId, rewards]);

  const handleSave = async () => {
    setLoading(true);

    try {
      const formData = new FormData();
      const rewardData = {
        rewardId: editRewardId || 0,
        sellerId: id,
        productId: selectedProducts,
        rewardName,
        rewardType,
        rewardDescription: "",
        rewardQuantity: rewardItems.length,
        startDate,
        endDate,
        rewardItemsList: rewardItems,
      };

      formData.append("reward", JSON.stringify(rewardData));

      rewardItems.forEach((item, index) => {
        if (item.file) {
          formData.append(`rewardItemsList[${index}].files`, item.file);
        } else if (item.itemImageURL) {
          formData.append(`rewardItemsList[${index}].files`, item.itemImageURL);
        }
      });

      const response = editRewardId
        ? await editReward(formData, idToken)
        : await addReward(formData, idToken);

      const data = {
        ...response.data,
        rewardItems: response.data2,
      };

      dispatch(editRewardId ? updateReward(data) : addNewReward(data));
      toast.success(
        `Reward ${editRewardId ? "updated" : "added"} successfully`
      );
      resetForm();
      toggle();
    } catch (error) {
      toast.error(`Error ${editRewardId ? "updating" : "adding"} reward`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRewardName("");
    setRewardType("General Reward");
    setSelectedProducts("");
    setStartDate("");
    setEndDate("unlimited");
    setRewardItems([initialRewardItem]);
  };

  return (
    <Modal
      isOpen={openModal}
      toggle={toggle}
      centered
      fullscreen="sm"
      contentClassName={styles.modal_content}
      className={styles.modal_product_container}
      wrapClassName={styles.modal_wrapper}
    >
      <AddRewardItemModal
        openModal={isRewardItemModalOpen}
        toggle={() => setIsRewardItemModalOpen(!isRewardItemModalOpen)}
        setRewardItems={setRewardItems}
        rewardItems={rewardItems}
      />
      <ModalBody className={styles.modal_location_body}>
        <div className={styles.modal_add_reward_header}>
          <h2 className={styles.modal_add_reward_header_title}>
            {editRewardId ? "Edit Reward" : "Add Reward"}
          </h2>
          <p className={styles.modal_add_reward_header_description}>
            {editRewardId
              ? "Edit the details of the reward you want to update."
              : "Enter the details of the reward you want to add."}
          </p>
        </div>

        <div className={styles.modal_add_reward_form}>
          <FormInput
            label="Reward Name"
            value={rewardName}
            onChange={(e) => setRewardName(e.target.value)}
            placeholder="Reward Name"
          />
          <FormSelect
            label="Products"
            value={selectedProducts}
            onChange={(e) => setSelectedProducts(e.target.value)}
            options={newProducts?.map((product) => ({
              value: product.id,
              label: product.name,
            }))}
          />
          <FormSelect
            label="Reward Type"
            value={rewardType}
            onChange={(e) => setRewardType(e.target.value)}
            options={[{ value: "General Reward", label: "General Reward" }]}
          />
          <Row>
            <Col md={6}>
              <div className={styles.modal_add_reward_form_field}>
                <label
                  htmlFor="startDate"
                  className={styles.modal_add_reward_form_label}
                >
                  Start Date
                </label>
                <div className={styles.modal_add_reward_form_input_wrapper}>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate?.split("T")[0]}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={styles.modal_add_reward_form_input}
                  />
                </div>
              </div>
            </Col>
            <Col md={6}>
              {endDate === "unlimited" ? (
                <FormSelect
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  options={[
                    { value: "unlimited", label: "Unlimited" },
                    { value: "limited", label: "Limited" },
                  ]}
                />
              ) : (
                <div className={styles.modal_add_reward_form_field}>
                  <label
                    htmlFor="endDate"
                    className={styles.modal_add_reward_form_label}
                  >
                    End Date
                  </label>
                  <div className={styles.modal_add_reward_form_input_wrapper}>
                    <input
                      type="date"
                      id="endDate"
                      value={endDate?.split("T")[0]}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={styles.modal_add_reward_form_input}
                    />
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </div>

        <div className={styles.modal_add_reward_table}>
          <div className={styles.modal_add_reward_table_header}>
            <h2 className={styles.modal_add_reward_table_header_title}>
              Rewards
            </h2>
            <button
              className={styles.modal_add_reward_table_header_button}
              onClick={() => setIsRewardItemModalOpen(true)}
            >
              Add Reward Item
            </button>
          </div>
          <RewardItemTable
            rewards={rewardItems}
            setOpenRewardItemModal={setIsRewardItemModalOpen}
          />
        </div>

        <div className={styles._button_wrapper}>
          <button className={styles._cancel_button} onClick={toggle}>
            Cancel
          </button>
          <button className={styles._save_button} onClick={handleSave}>
            Save
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddProductModal;
